// @flow

import * as React from 'react';
import classNames from 'classnames';
import {
  DropDownSelector,
  TabSelector,
  ControlledContentGroup,
  UncontrolledContentGroup,
} from 'docs/components';
import {usePrefs} from 'docs/data';
import {ContentGroupItem} from 'docs/util';
import {useContentAnimation} from 'docs/shell/content/ContentAnimationProvider';
import './TabGroup.scss';

type TabGroupSelectorProps = {|
  label?: string,
  type: string,
|};

const TabGroupSelector = ({label, type}: TabGroupSelectorProps) => {
  switch (type) {
    case 'drop-down':
      return <DropDownSelector label={label} />;
    case 'tabs':
      return (
        <TabSelector label={label} size="medium" tabClassName="TabGroup-tab" />
      );
    case 'none':
      return null;
    default:
      throw new Error(`Unknown selector type ${type} for TabGroup`);
  }
};

type TabGroupProps = {|
  label?: string,
  items: Array<ContentGroupItem>,
  pref?: string,
  defaultValue?: string,
  selector: string,
|};

export const TabGroup = ({
  label,
  items,
  pref,
  defaultValue,
  selector = 'tabs',
}: TabGroupProps) => {
  const {getPref, setPref} = usePrefs();
  const selectorComponent = <TabGroupSelector type={selector} label={label} />;
  const {setAnimatePref} = useContentAnimation();

  // If no pref is specified, the tab group is "uncontrolled", meaning it manages
  // its state internally.
  if (!pref) {
    return (
      <UncontrolledContentGroup
        className={classNames('TabGroup', `TabGroup--${selector}`)}
        items={items}
        initialSelectedId={defaultValue}
        selector={selectorComponent}
      />
    );
  }

  return (
    <ControlledContentGroup
      className="TabGroup"
      items={items}
      selector={selectorComponent}
      selectedId={getPref(pref) || defaultValue}
      onItemSelected={(item) => {
        setPref(pref, item.id);
        setAnimatePref(pref);
      }}
      pref={pref}
    />
  );
};
